import { useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useClient } from 'urql';
import CodeInput from '@/components/Custom/CodeInput';
import Text from '@/components/Unistyles/Text';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import { graphql } from '@/gql/graphql';
import {
  resetWelcomeInviteDraft,
  type WelcomeFormValues,
} from '@/layouts/Welcome/form';
import { WELCOME_COMPLETION_ROUTE } from '@/layouts/Welcome/navigation';
import JoinKindSelector from '@/screens/Join/JoinKindSelector';
import useInvitePreview from '@/screens/Join/useInvitePreview';
import useJoinIntentStore, {
  type JoinIntent,
  type JoinKind,
} from '@/stores/useJoinIntentStore';
import { getJoinKindConfiguration } from '@/utils/joinKind';

const PreviewManualJoin = graphql(/* GraphQL */ `
  query WelcomeJoinPreviewInvite($kind: invite_entry_kind!, $code: String!) {
    invite_entry_preview(args: { kind: $kind, code: $code }) {
      entity_id
      status
    }
  }
`);

export default function WelcomeJoin() {
  const joinIntent = useJoinIntentStore((state) => state.joinIntent);
  const joinResumePending = useJoinIntentStore(
    (state) => state.joinResumePending,
  );

  if (joinIntent && joinResumePending) {
    return <StoredJoin joinIntent={joinIntent} />;
  }

  return <ManualJoin />;
}

function StoredJoin({ joinIntent }: { joinIntent: JoinIntent }) {
  const router = useRouter();
  const clearJoinIntent = useJoinIntentStore((state) => state.clearJoinIntent);
  const {
    preview,
    error: previewError,
    fetching,
    received,
    retry: retryPreview,
  } = useInvitePreview(joinIntent.kind, joinIntent.code);
  const isChecking = fetching;
  const hasCheckError = !isChecking && Boolean(previewError);
  const isAvailable = Boolean(preview && preview.status === 'available');
  const isUnavailable =
    !isChecking && !hasCheckError && received && !isAvailable;

  const continueWithoutJoin = () => {
    clearJoinIntent();
    router.replace('/welcome/profile');
  };
  const continueWithJoin = () => {
    router.replace('/welcome/profile');
  };
  const retryJoinCheck = () => {
    retryPreview({ requestPolicy: 'network-only' });
  };

  return (
    <WelcomeStepFrame
      title={
        hasCheckError
          ? "We couldn't check this invitation"
          : isUnavailable
            ? 'This invitation is no longer available'
            : preview?.title
              ? `Welcome to\n${preview.title}`
              : 'Checking your invitation'
      }
      subtitle={
        hasCheckError
          ? 'Check your connection and try again.'
          : isUnavailable
            ? 'You can still create your profile without joining.'
            : preview?.title
              ? 'Create your profile to finish joining.'
              : 'Checking your invitation...'
      }
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label={
              hasCheckError
                ? 'Try again'
                : isUnavailable
                  ? 'Create profile'
                  : isChecking
                    ? 'Checking...'
                    : 'Continue'
            }
            loading={isChecking}
            loadingLabel="Checking…"
            disabled={isChecking}
            onPress={
              hasCheckError
                ? retryJoinCheck
                : isUnavailable
                  ? continueWithoutJoin
                  : continueWithJoin
            }
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>{null}</WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}

function ManualJoin() {
  const { code } = useLocalSearchParams<{ code?: string }>();
  const { navigate } = useRouter();
  const client = useClient();
  const [kind, setKind] = useState<JoinKind>('team');
  const setJoinIntent = useJoinIntentStore((state) => state.setJoinIntent);
  const clearJoinIntent = useJoinIntentStore((state) => state.clearJoinIntent);
  const { control, formState, setValue } = useFormContext<WelcomeFormValues>();
  const inviteCode = useWatch({ control, name: 'inviteCode' });
  const routeInviteCode = code?.trim().toLowerCase() || '';
  const activeInviteCode =
    formState.dirtyFields.inviteCode || !routeInviteCode
      ? inviteCode
      : routeInviteCode;
  const normalizedInviteCode = activeInviteCode.trim().toLowerCase();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCheckingInviteCode, setIsCheckingInviteCode] = useState(false);
  const canContinue =
    normalizedInviteCode.length === 8 && !isCheckingInviteCode;

  const setFormInviteCode = (nextInviteCode: string) => {
    setErrorMessage(null);
    setValue('inviteCode', nextInviteCode.replace(/\s+/g, '').toLowerCase(), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onPressSkip = () => {
    clearJoinIntent();
    resetWelcomeInviteDraft(setValue);
    navigate(WELCOME_COMPLETION_ROUTE);
  };

  const onPressContinue = async () => {
    if (!canContinue) {
      return;
    }

    setErrorMessage(null);
    setIsCheckingInviteCode(true);

    const { data, error } = await client.query(
      PreviewManualJoin,
      {
        code: normalizedInviteCode,
        kind,
      },
      { requestPolicy: 'network-only' },
    );

    setIsCheckingInviteCode(false);

    if (error) {
      setErrorMessage('Could not check that invite code. Please try again.');
      return;
    }

    const entry = data?.invite_entry_preview[0];
    if (!entry?.entity_id || entry.status !== 'available') {
      setErrorMessage('Invalid invite code. Please try again.');
      return;
    }

    setValue('inviteCode', normalizedInviteCode, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setJoinIntent({
      kind,
      code: normalizedInviteCode,
      memberType: getJoinKindConfiguration(kind).defaultMemberType,
    });
    navigate(WELCOME_COMPLETION_ROUTE);
  };

  return (
    <WelcomeStepFrame
      title={kind === 'team' ? 'Join a team' : 'Join an organization'}
      subtitle={`Enter ${kind === 'team' ? 'a team' : 'an organization'} invite code. You can also skip for now and join later.`}
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label="Continue"
            loading={isCheckingInviteCode}
            loadingLabel="Checking…"
            disabled={!canContinue}
            onPress={() => void onPressContinue()}
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>
        <JoinKindSelector
          value={kind}
          onValueChange={(nextKind) => {
            setErrorMessage(null);
            setKind(nextKind);
          }}
        />
        <WelcomeCard>
          <CodeInput
            isDisabled={isCheckingInviteCode}
            value={activeInviteCode.toUpperCase()}
            onChange={setFormInviteCode}
          />
          {errorMessage ? (
            <Text size="sm" variant="error" center selectable>
              {errorMessage}
            </Text>
          ) : null}
        </WelcomeCard>
        <WelcomeActions>
          <WelcomeSecondaryAction
            label="I will join later"
            icon="arrow-forward-outline"
            onPress={onPressSkip}
          />
        </WelcomeActions>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}
